import React, {
  Ref, useEffect, useRef, useState,
} from 'react';
import {
  View, Text, StyleSheet, ActivityIndicator, TouchableOpacity, FlatList, Pressable, Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Modal from 'react-native-modal';
import * as Notifications from 'expo-notifications';
import { useSelector } from 'react-redux';
import Colors from '../constants/Colors';
import Style from '../constants/Style';
import Button from '../components/Button';
import FilterIcon16 from '../components/16pxIcons/FilterIcon';
import NotificationItem from '../components/NotificationsLog/NotificationItem';
import getNotifications from '../requests/Notifications/GetNotifications';
import NotificationFilter from '../components/NotificationsLog/NotificationFilter';
import NotificationSearch from '../components/NotificationsLog/NotificationSearch';
import AlertModal from '../components/AlertModal';
import isMobile from '../components/IsMobile';
import LeftArrow16 from '../components/16pxIcons/LeftArrow16';
import ScreenEmptyState from '../components/ScreenEmptyState';
import NoNotificationsImage from '../components/NotificationsLog/NoNotificationsImage';
import FilterTags from '../components/NotificationsLog/FilterTags';
import { DateRange } from '../redux/dateRangeSlice';
import { Notification, getNotificationData } from '../components/NotificationsLog/data';
import {
  FILTER_RESULTS_LOAD_FAILURE, NOTIFICATIONS_LOAD_ERROR, SEARCH_RESULTS_LOAD_FAILURE,
} from '../constants/AlertMessages';
import SkeletonLoader from '../components/SkeletonLoader';
import {
  NotificationSeverity, NotificationType, TicketPriority, TicketStatus,
} from '../constants/Types';
import { isValidValue } from '../constants/Utils';
import { RootTabScreenProps } from '../types';
import { AppScreen } from '../navigation/LinkingConfiguration';
import { BuildingSettingsPriority } from './LocationManagement/components/BuildingSettingsModal/data';

const NotificationsLogScreen = ({
  navigation, route,
}: RootTabScreenProps<AppScreen.NOTIFICATIONS_LOG>) => {
  const limit: number = 10;
  const [dataOffset, setDataOffset] = useState<number>(0);
  const [filteredDataOffset, setFilteredDataOffset] = useState<number>(0);
  const [data,
    setData] = useState<Notification[]>((isMobile ? (route?.params as any)?.data : []) ?? []);
  const [filteredData, setFilteredData] = useState<Notification[]>(null);
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const notEmpty: boolean | undefined | null = useSelector((state:
    { emptyState: { notEmpty: boolean } }) => state.emptyState.notEmpty);
  const [clientId, setClientId] = useState<string>('');
  const [filterShown, setFilterShown] = useState<boolean>(false);
  const [filterToggleEnabled, setFilterToggleEnabled] = useState<boolean>(true);
  const [searchString, setSearchString] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<DateRange>({ start: null, end: null });
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority>(null);
  const [severityFilter, setSeverityFilter] = useState<NotificationSeverity>(null);
  const [notificationTypeFilter, setNotificationTypeFilter] = useState<NotificationType>(null);
  const [progressionFilter, setProgressionFilter] = useState<TicketStatus>(null);
  const [appliedSearchString, setAppliedSearchString] = useState<string>('');
  const [appliedDateFilter, setAppliedDateFilter] = useState<DateRange>({ start: null, end: null });
  const [appliedPriorityFilter, setAppliedPriorityFilter] = useState<TicketPriority>(null);
  const [appliedSeverityFilter, setAppliedSeverityFilter] = useState<NotificationSeverity>(null);
  const [appliedNotificationTypeFilter,
    setAppliedNotificationTypeFilter] = useState<NotificationType>(null);
  const [appliedProgressionFilter, setAppliedProgressionFilter] = useState<TicketStatus>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [paginationLoading, setPaginationLoading] = useState<boolean>(true);
  const [reachedEnd, setReachedEnd] = useState<boolean>(false);
  const [filteredDataReachedEnd, setFilteredDataReachedEnd] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string>(null);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [filterAlertTitle, setFilterAlertTitle] = useState<string>('');
  const [filterAlertMessage, setFilterAlertMessage] = useState<string>(null);
  const [filterAlertVisible, setFilterAlertVisible] = useState<boolean>(false);
  const insets = useSafeAreaInsets();
  const notificationList: Ref<FlatList> = useRef<FlatList>();
  const slaNames: string[] = useSelector(
    (state: { selectedBuilding: { SlaHours: BuildingSettingsPriority[] } }) => state
      .selectedBuilding.SlaHours?.map((item: BuildingSettingsPriority) => item.Name),
  );
  const filterApplied: boolean = !!appliedDateFilter.start || !!appliedDateFilter.end
    || isValidValue(appliedPriorityFilter, TicketPriority) || !!appliedNotificationTypeFilter
    || !!appliedProgressionFilter || isValidValue(appliedSeverityFilter, NotificationSeverity);

  const showAlert = (
    title: string,
    message: string,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = null,
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const showFilterAlert = (title: string, message: string) => {
    if (isMobile) {
      setFilterAlertTitle(title);
      setFilterAlertMessage(message);
      setFilterAlertVisible(true);
      setLoading(false);
      setPaginationLoading(false);
    } else showAlert(title, message);
  };

  const loadError = (errorText?: string) => {
    setData([]);
    setLoading(false);
    setPaginationLoading(false);
    showFilterAlert('Error', errorText ?? NOTIFICATIONS_LOAD_ERROR);
  };

  const filterError = (errorText?: string) => {
    showFilterAlert('Error', errorText ?? FILTER_RESULTS_LOAD_FAILURE);
  };

  const searchError = (errorText?: string) => showAlert(
    'Error',
    errorText ?? SEARCH_RESULTS_LOAD_FAILURE,
  );

  useEffect(() => {
    AsyncStorage.getItem('clientName').then(setClientId);
    const unsubscribe = navigation.addListener('blur', hideFilter);
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!(dataOffset > 0 && !loading && !reachedEnd && !filterApplied)) return;
    setPaginationLoading(true);
    getNotifications({ buildingId, limit, offset: dataOffset }).then((res) => {
      if (res && !res.isError && res.Notifications) {
        if (res.Notifications.length === 0) setReachedEnd(true);
        setData([...data, ...getNotificationData(res.Notifications)]);
      } else loadError(res?.errorText);
      setPaginationLoading(false);
    }).catch((error) => loadError(error?.message));
  }, [dataOffset]);

  useEffect(() => {
    if (!(filteredDataOffset > 0 && !loading && !filteredDataReachedEnd && filterApplied)) return;
    setPaginationLoading(true);
    getNotifications({
      buildingId,
      limit,
      offset: filteredDataOffset,
      startDate: appliedDateFilter.start,
      endDate: appliedDateFilter.end,
      priority: appliedPriorityFilter,
      type: appliedNotificationTypeFilter,
      progression: appliedProgressionFilter,
      severity: appliedSeverityFilter,
    }).then((res) => {
      if (res && !res.isError && res.Notifications) {
        if (res.Notifications.length === 0) setFilteredDataReachedEnd(true);
        setFilteredData([...filteredData, ...getNotificationData(res.Notifications)]);
      } else loadError(res?.errorText);
      setPaginationLoading(false);
    }).catch((error) => loadError(error?.message));
  }, [filteredDataOffset]);

  const loadData = () => {
    if (!buildingId) return;
    setDateFilter(appliedDateFilter);
    setPriorityFilter(appliedPriorityFilter);
    setNotificationTypeFilter(appliedNotificationTypeFilter);
    setProgressionFilter(appliedProgressionFilter);
    setSeverityFilter(appliedSeverityFilter);
    setLoading(true);
    if (filterApplied) {
      applyFilter(
        appliedDateFilter,
        appliedPriorityFilter,
        appliedNotificationTypeFilter,
        appliedProgressionFilter,
        appliedSeverityFilter,
      );
    }
    getNotifications({ buildingId, limit, offset: dataOffset }).then((res) => {
      if (res && !res.isError && res.Notifications) {
        setData(getNotificationData(res.Notifications));
        setLoading(false);
      } else loadError(res?.errorText);
    }).catch((error) => loadError(error?.message));
  };

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', loadData);
    return unsubscribe;
  }, [buildingId, appliedDateFilter, appliedPriorityFilter, appliedSeverityFilter,
    appliedNotificationTypeFilter, appliedProgressionFilter]);

  useEffect(() => {
    const subscription = Notifications.addNotificationReceivedListener(loadData) as any;
    return () => subscription.remove();
  }, [buildingId]);

  const search = () => {
    setLoading(true);
    getNotifications({
      limit,
      offset: 0,
      buildingId,
      // searchString.trim(),
      startDate: appliedDateFilter.start,
      endDate: appliedDateFilter.end,
      priority: appliedPriorityFilter,
      type: appliedNotificationTypeFilter,
      progression: appliedProgressionFilter,
      severity: appliedSeverityFilter,
      textsearch: searchString.trim().length > 0 ? searchString.trim() : null,
    }).then((res) => {
      if (res && !res.isError && res.Notifications) {
        setFilteredData(getNotificationData(res.Notifications));
        setAppliedSearchString(searchString.trim());
        setLoading(false);
        setPaginationLoading(false);
        setDataOffset(0);
        setSearchString('');
      } else searchError(res?.errorText);
    }).catch((error) => searchError(error?.message));
  };

  const applyFilter = (
    date: DateRange,
    priority?: TicketPriority,
    notificationType?: NotificationType,
    progression?: TicketStatus,
    severity?: number,
  ) => {
    setLoading(true);
    getNotifications({
      limit,
      offset: 0,
      buildingId,
      textsearch: appliedSearchString?.trim().length > 0 ? appliedSearchString.trim() : null,
      startDate: date.start,
      endDate: date.end,
      priority,
      type: notificationType,
      progression,
      severity,
    }).then((res) => {
      if (res && !res.isError && res.Notifications) {
        setFilteredDataReachedEnd(false);
        setFilteredData(getNotificationData(res.Notifications));
        hideFilter();
        setAppliedDateFilter(date);
        setAppliedPriorityFilter(priority);
        setAppliedNotificationTypeFilter(notificationType);
        setAppliedProgressionFilter(progression);
        setAppliedSeverityFilter(severity);
        setLoading(false);
        setPaginationLoading(false);
      } else filterError(res?.errorText);
    }).catch((error) => filterError(error?.message));
  };

  const clearFilterResults = () => {
    const onSuccess: () => void = () => {
      setDateFilter({ start: null, end: null });
      setPriorityFilter(null);
      setSeverityFilter(null);
      setNotificationTypeFilter(null);
      setProgressionFilter(null);
      setAppliedDateFilter({ start: null, end: null });
      setAppliedPriorityFilter(null);
      setAppliedSeverityFilter(null);
      setAppliedNotificationTypeFilter(null);
      setAppliedProgressionFilter(null);
      setLoading(false);
      setPaginationLoading(false);
      setDataOffset(0);
      setFilteredDataOffset(0);
    };
    setLoading(true);
    if (appliedSearchString.length > 0) {
      getNotifications({
        limit,
        offset: 0,
        buildingId,
        textsearch: appliedSearchString.length > 0 ? appliedSearchString : null,
      }).then((res) => {
        if (res && !res.isError && res.Notifications) {
          setFilteredData(getNotificationData(res.Notifications));
          onSuccess();
        } else filterError(res?.errorText);
      }).catch((error) => filterError(error?.message));
    } else {
      setFilteredData(null);
      onSuccess();
    }
  };

  const clearSearchResults = () => {
    setLoading(true);
    if (isValidValue(appliedPriorityFilter, TicketPriority)
      || isValidValue(appliedSeverityFilter, NotificationSeverity)
      || appliedNotificationTypeFilter || appliedProgressionFilter
      || appliedDateFilter.start || appliedDateFilter.end) {
      getNotifications({
        limit,
        offset: dataOffset,
        buildingId,
        startDate: appliedDateFilter.start,
        endDate: appliedDateFilter.end,
        priority: appliedPriorityFilter,
        type: appliedNotificationTypeFilter,
        progression: appliedProgressionFilter,
        severity: appliedSeverityFilter,
      }).then((res) => {
        if (res && !res.isError && res.Notifications) {
          setFilteredData(getNotificationData(res.Notifications));
          setAppliedSearchString('');
          setDataOffset(0);
          setFilteredDataOffset(0);
          setLoading(false);
          setPaginationLoading(false);
        } else filterError(res?.errorText);
      }).catch((error) => filterError(error?.message));
    } else {
      setFilteredData(null);
      setAppliedSearchString('');
      setLoading(false);
      setPaginationLoading(false);
    }
  };

  const clearDateFilter = () => {
    setLoading(true);
    if (isValidValue(appliedPriorityFilter, TicketPriority)
      || isValidValue(appliedSeverityFilter, NotificationSeverity)
      || appliedNotificationTypeFilter || appliedProgressionFilter) {
      getNotifications({
        limit,
        offset: 0,
        buildingId,
        textsearch: appliedSearchString?.trim().length > 0 ? appliedSearchString.trim() : null,
        priority: appliedPriorityFilter,
        type: appliedNotificationTypeFilter,
        progression: appliedProgressionFilter,
        severity: appliedSeverityFilter,
      }).then((res) => {
        if (res && !res.isError && res.Notifications) {
          setFilteredData(getNotificationData(res.Notifications));
          setAppliedDateFilter({ start: null, end: null });
          setDateFilter({ start: null, end: null });
          setDataOffset(0);
          setFilteredDataOffset(0);
          setLoading(false);
          setPaginationLoading(false);
        } else filterError(res?.errorText);
      }).catch((error) => filterError(error?.message));
    } else clearFilterResults();
  };

  const clearPriorityFilter = () => {
    setLoading(true);
    if (appliedDateFilter?.start || appliedDateFilter?.end
      || isValidValue(appliedSeverityFilter, NotificationSeverity)
      || appliedNotificationTypeFilter || appliedProgressionFilter) {
      getNotifications({
        limit,
        offset: 0,
        buildingId,
        textsearch: appliedSearchString?.trim().length > 0 ? appliedSearchString.trim() : null,
        startDate: appliedDateFilter?.start,
        endDate: appliedDateFilter?.end,
        type: appliedNotificationTypeFilter,
        progression: appliedProgressionFilter,
        severity: appliedSeverityFilter,
      }).then((res) => {
        if (res && !res.isError && res.Notifications) {
          setFilteredData(getNotificationData(res.Notifications));
          setAppliedPriorityFilter(null);
          setPriorityFilter(null);
          setDataOffset(0);
          setFilteredDataOffset(0);
          setLoading(false);
          setPaginationLoading(false);
        } else filterError(res?.errorText);
      }).catch((error) => filterError(error?.message));
    } else clearFilterResults();
  };

  const clearSeverityFilter = () => {
    setLoading(true);
    if (appliedDateFilter?.start || appliedDateFilter?.end
      || isValidValue(appliedPriorityFilter, TicketPriority)
      || appliedNotificationTypeFilter || appliedProgressionFilter) {
      getNotifications({
        limit,
        offset: 0,
        buildingId,
        textsearch: appliedSearchString?.trim().length > 0 ? appliedSearchString.trim() : null,
        startDate: appliedDateFilter?.start,
        endDate: appliedDateFilter?.end,
        priority: appliedPriorityFilter,
        type: appliedNotificationTypeFilter,
        progression: appliedProgressionFilter,
        severity: appliedSeverityFilter,
      }).then((res) => {
        if (res && !res.isError && res.Notifications) {
          setFilteredData(getNotificationData(res.Notifications));
          setAppliedSeverityFilter(null);
          setSeverityFilter(null);
          setDataOffset(0);
          setFilteredDataOffset(0);
          setLoading(false);
          setPaginationLoading(false);
        } else filterError(res?.errorText);
      }).catch((error) => filterError(error?.message));
    } else clearFilterResults();
  };

  const clearNotificationTypeFilter = () => {
    setLoading(true);
    if (appliedDateFilter?.start || appliedDateFilter?.end
      || isValidValue(appliedSeverityFilter, NotificationSeverity)
      || isValidValue(appliedPriorityFilter, TicketPriority) || appliedProgressionFilter) {
      getNotifications({
        limit,
        offset: 0,
        buildingId,
        textsearch: appliedSearchString?.trim().length > 0 ? appliedSearchString.trim() : null,
        startDate: appliedDateFilter?.start,
        endDate: appliedDateFilter?.end,
        priority: appliedPriorityFilter,
        progression: appliedProgressionFilter,
        severity: appliedSeverityFilter,
      }).then((res) => {
        if (res && !res.isError && res.Notifications) {
          setFilteredData(getNotificationData(res.Notifications));
          setAppliedNotificationTypeFilter(null);
          setNotificationTypeFilter(null);
          setDataOffset(0);
          setFilteredDataOffset(0);
          setLoading(false);
          setPaginationLoading(false);
        } else filterError(res?.errorText);
      }).catch((error) => filterError(error?.message));
    } else clearFilterResults();
  };

  const clearProgressionFilter = () => {
    setLoading(true);
    if (appliedDateFilter?.start || appliedDateFilter?.end
      || isValidValue(appliedSeverityFilter, NotificationSeverity)
      || appliedNotificationTypeFilter || isValidValue(appliedPriorityFilter, TicketPriority)) {
      getNotifications({
        limit,
        offset: 0,
        buildingId,
        textsearch: appliedSearchString?.trim().length > 0 ? appliedSearchString.trim() : null,
        startDate: appliedDateFilter?.start,
        endDate: appliedDateFilter?.end,
        priority: appliedPriorityFilter,
        type: appliedNotificationTypeFilter,
        severity: appliedSeverityFilter,
      }).then((res) => {
        if (res && !res.isError && res.Notifications) {
          setFilteredData(getNotificationData(res.Notifications));
          setAppliedProgressionFilter(null);
          setProgressionFilter(null);
          setDataOffset(0);
          setFilteredDataOffset(0);
          setLoading(false);
          setPaginationLoading(false);
        } else filterError(res?.errorText);
      }).catch((error) => filterError(error?.message));
    } else clearFilterResults();
  };

  const formatDate = (dateString: string) => {
    const date: Date = new Date(dateString);
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear().toString()
      .substring(2)}`;
  };

  const hideFilter = () => setFilterShown(false);

  const showFilter = () => {
    setFilterShown(true);
    setFilterToggleEnabled(false);
    const timeout = setTimeout(() => {
      setFilterToggleEnabled(true);
      clearTimeout(timeout);
    }, 375);
  };

  const groupedData = (filteredData ?? data)
    .sort((a: Notification, b: Notification) => b.timestamp.localeCompare(a.timestamp))
    .reduce((dates: object, item: Notification) => {
      const key: string = formatDate(item.timestamp);
      const group: Notification[] = dates[key] ?? [];
      group.push(item);
      dates[key] = group;
      return dates;
    }, {});

  const filterMenu = () => <NotificationFilter
    {...{
      slaNames,
      applyFilter,
      filterShown,
      hideFilter,
      dateFilter,
      setDateFilter,
      priorityFilter,
      setPriorityFilter,
      severityFilter,
      setSeverityFilter,
      notificationTypeFilter,
      setNotificationTypeFilter,
      progressionFilter,
      setProgressionFilter,
      loading,
      filterAlertTitle,
      filterAlertMessage,
      filterAlertVisible,
      filterAlertToggle: setFilterAlertVisible,
    }}
  />;

  const content = () => <View style={[
    styles.container,
    isMobile && {
      paddingTop: styles.container.paddingTop,
      paddingBottom: styles.container.paddingBottom + insets.bottom,
    },
  ]}>
    <View style={styles.titleSection}>
      {isMobile && <TouchableOpacity
        style={styles.backButton}
        onPress={navigation.goBack}
      >
        <LeftArrow16 />
      </TouchableOpacity>}
      <Text style={styles.title}>{'Notifications log'}</Text>
    </View>
    {notEmpty ? <>
      <NotificationSearch
        {...{ search }}
        value={searchString}
        onChangeText={setSearchString}
      />
      <View style={styles.content}>
        <View style={[Style.row, styles.header]}>
          <View style={Style.row}>
            <Text style={styles.subTitle}>{'Notifications:'}</Text>
          </View>
          <Button
            title={isMobile ? '' : 'Filter'}
            onPress={showFilter}
            style={styles.filterButton}
            children={<FilterIcon16 style={styles.filterIcon} />}
            testID='filter-notifications-btn'
          />
        </View>
        <FilterTags
          {...{
            slaNames,
            appliedDateFilter,
            appliedNotificationTypeFilter,
            appliedPriorityFilter,
            appliedProgressionFilter,
            appliedSeverityFilter,
            appliedSearchString,
            clearFilterResults,
            clearSearchResults,
            clearDateFilter,
            clearPriorityFilter,
            clearProgressionFilter,
            clearNotificationTypeFilter,
            clearSeverityFilter,
          }}
        />
        <FlatList
          onScrollToIndexFailed={() => { }}
          onEndReached={() => {
            if (filteredData) setFilteredDataOffset(filteredDataOffset + limit);
            else setDataOffset(dataOffset + limit);
          }}
          style={styles.list}
          contentContainerStyle={styles.listContent}
          ref={notificationList}
          data={Object.keys(groupedData) ?? []}
          renderItem={({ item: key }) => <View>
            <Text style={styles.dateTitle}>
              {key}
            </Text>
            <FlatList
              data={groupedData[key]}
              scrollEnabled={false}
              renderItem={({ item }) => <NotificationItem {...{ ...item, clientId }} />}
              keyExtractor={(item: Notification) => `notification_item_${key}_${item.id}`}
            />
          </View>}
          keyExtractor={(item) => `notification_item_section_${item}`}
          ListEmptyComponent={!loading && <ScreenEmptyState
            image={<NoNotificationsImage />}
            title='No notifications'
            subtitle='You currently have no new notifications. The system will notify you when it updates'
          />}
          ListFooterComponent={paginationLoading ? <SkeletonLoader
            delay={100} height={200} /> : null}
        />
        {!isMobile && filterMenu()}
      </View>
      {Platform.OS !== 'android' && <ActivityIndicator
        size='large'
        style={Style.activityIndicator}
        animating={loading}
      />}
      {isMobile && <Modal
        style={[Style.modal, {
          position: 'absolute', alignSelf: 'center', bottom: 0, maxHeight: '90%',
        }]}
        animationIn='fadeIn'
        animationOut='fadeOut'
        isVisible={filterShown}
      >
        {filterMenu()}
      </Modal>}
      <AlertModal
        title={alertTitle}
        message={alertMessage}
        error={alertError}
        modalVisible={alertVisible}
        setModalVisible={setAlertVisible}
        action={alertAction}
        actionTitle={alertActionTitle}
      />
    </> : <ScreenEmptyState
      large
      image={<NoNotificationsImage />}
    />}
  </View>;

  return isMobile ? content() : <Pressable
    onPress={filterToggleEnabled ? hideFilter : null}
    children={content()}
    style={{
      flex: 1,
      cursor: 'auto',
    }}
  />;
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#F8FAFB',
    flex: 1,
    paddingTop: isMobile ? 52 : 32,
    paddingStart: isMobile ? 24 : 40,
    paddingEnd: isMobile ? 24 : 25,
    paddingBottom: isMobile ? 9 : 40,
  },
  title: {
    fontSize: isMobile ? 22 : 32,
    lineHeight: isMobile ? 33 : 48,
    color: Colors.light.hereworksBlack600,
    fontFamily: isMobile ? 'Poppins_600SemiBold' : 'Poppins_400Regular',
  },
  content: {
    paddingBottom: 24,
    backgroundColor: '#FFF',
    flex: 1,
    borderRadius: 24,
    overflow: 'hidden',
  },
  header: {
    padding: isMobile ? 20 : 24,
  },
  subTitle: {
    fontSize: 22,
    lineHeight: 33,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
    marginEnd: 4,
  },
  filterButton: {
    paddingHorizontal: isMobile ? undefined : 24,
    height: 32,
    width: isMobile ? 32 : undefined,
    alignItems: 'center',
    justifyContent: 'center',
    flex: undefined,
  },
  filterIcon: {
    marginStart: isMobile ? 0 : 9,
  },
  list: {
    paddingHorizontal: isMobile ? 20 : 24,
    borderTopWidth: isMobile ? 1 : 0,
    borderTopColor: isMobile ? Colors.light.hereworksBlue100 : undefined,
  },
  listContent: {
    minHeight: '70%',
  },
  dateTitle: {
    fontSize: isMobile ? 16 : 18,
    lineHeight: isMobile ? 24 : 27,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginVertical: 17,
  },
  titleSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: isMobile ? 32 : 24,
  },
  backButton: {
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 5,
    borderColor: Colors.light.hereworksBlue400,
    width: 32,
    height: 32,
    backgroundColor: '#FFF',
    marginEnd: 12,
  },
  loadingIndicator: {
    marginTop: 12,
  },
});

export default NotificationsLogScreen;
